// src/components/sales/pages/sales-templates/SalesTemplateModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XCircle, CheckCircle } from "lucide-react";
import ModalPortal from "../../../shared/ui/ModalPortal";
import { salesTemplatesService } from "../../../../services/salesTemplates.service";

const CHANNEL_OPTIONS = [
  { value: "Email", labelKey: "sales.salesTemplates.channels.email" },
  { value: "WhatsApp", labelKey: "sales.salesTemplates.channels.whatsApp" },
  { value: "SMS", labelKey: "sales.salesTemplates.channels.sms" },
];

const empty = () => ({
  templateName: "",
  subject: "",
  content: "",
  isActive: true,
});

const norm = (v) => String(v ?? "").trim();

export default function SalesTemplateModal({ open, editing, onClose, onSaved }) {
  const { t, i18n } = useTranslation();
  void i18n;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty());

  // Channel select + optional custom
  const [channelSelect, setChannelSelect] = useState("Email");
  const [channelCustom, setChannelCustom] = useState("");

  const channelType =
    channelSelect === "CUSTOM" ? norm(channelCustom) : norm(channelSelect);

  useEffect(() => {
    if (!open) return;

    const ch = editing?.channelType ?? editing?.channel ?? "";
    const known = ["Email", "WhatsApp", "SMS"];
    const isKnown = known.includes(ch);

    setChannelSelect(isKnown ? ch : ch ? "CUSTOM" : "Email");
    setChannelCustom(!isKnown ? ch : "");

    if (editing) {
      setForm({
        templateName: editing.templateName || editing.name || "",
        subject: editing.subject || "",
        content: editing.content || editing.body || "",
        isActive: typeof editing.isActive === "boolean" ? editing.isActive : true,
      });
    } else {
      setForm(empty());
    }
  }, [open, editing]);

  const title = useMemo(
    () =>
      editing
        ? t("sales.salesTemplates.modal.editTitle")
        : t("sales.salesTemplates.modal.addTitle"),
    [editing, t]
  );

  const validate = () => {
    if (!norm(form.templateName)) return t("sales.salesTemplates.validation.templateNameRequired");
    if (!norm(channelType)) return t("sales.salesTemplates.validation.channelTypeRequired");
    if (channelType === "Email" && !norm(form.subject))
      return t("sales.salesTemplates.validation.subjectRequiredForEmail");
    if (!norm(form.content)) return t("sales.salesTemplates.validation.contentRequired");
    return "";
  };

  const submit = async () => {
    const err = validate();
    if (err) return alert(err);

    const payload = {
      templateName: norm(form.templateName),
      channelType,
      subject: norm(form.subject), // optional for WhatsApp/SMS
      content: norm(form.content),
      isActive: !!form.isActive,
    };

    setSaving(true);
    try {
      const id = editing?.id ?? editing?.templateId ?? editing?.salesTemplateId;
      if (id != null) {
        await salesTemplatesService.update(id, payload);
      } else {
        await salesTemplatesService.create(payload);
      }
      await onSaved?.();
    } catch (e) {
      alert(e?.message || t("sales.salesTemplates.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <ModalPortal onClose={saving ? undefined : onClose}>
      <div className="fixed inset-0 z-[9999]">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md"
          onMouseDown={saving ? undefined : onClose}
        />

        {/* Centered Modal */}
        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
          <div
            className="relative w-full max-w-5xl my-10 bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.30)] rounded-2xl overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-700 flex justify-between items-center">
              <div className="min-w-0">
                <h2 className="text-2xl font-bold text-white">{title}</h2>
                <p className="text-sm text-white/80 mt-1 truncate">
                  {editing?.id
                    ? t("sales.salesTemplates.modal.templateId", { id: editing.id })
                    : t("sales.salesTemplates.modal.newTemplate")}
                </p>
              </div>

              <button
                onClick={saving ? undefined : onClose}
                disabled={saving}
                className="text-white hover:text-gray-200 disabled:opacity-50"
                aria-label={t("common.close")}
                type="button"
                title={t("common.close")}
              >
                <XCircle className="w-7 h-7" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto text-white">
              <div className="grid md:grid-cols-2 gap-4">
                <Box label={t("sales.salesTemplates.modal.templateNameLabel")}>
                  <input
                    value={form.templateName}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, templateName: e.target.value }))
                    }
                    className={inputCls}
                    placeholder={t("sales.salesTemplates.modal.templateNamePlaceholder")}
                    disabled={saving}
                  />
                </Box>

                <Box label={t("sales.salesTemplates.modal.channelTypeLabel")}>
                  <select
                    value={channelSelect}
                    onChange={(e) => setChannelSelect(e.target.value)}
                    className={inputCls}
                    disabled={saving}
                  >
                    {CHANNEL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} className="text-black">
                        {t(o.labelKey)}
                      </option>
                    ))}
                  </select>

                  {channelSelect === "CUSTOM" && (
                    <input
                      value={channelCustom}
                      onChange={(e) => setChannelCustom(e.target.value)}
                      className={inputCls + " mt-2"}
                      placeholder={t("sales.salesTemplates.modal.channelCustomPlaceholder")}
                      disabled={saving}
                    />
                  )}
                </Box>

                <Box
                  label={t("sales.salesTemplates.modal.subjectLabel", {
                    requirement:
                      channelType === "Email"
                        ? t("sales.salesTemplates.modal.subjectRequiredNote")
                        : t("sales.salesTemplates.modal.subjectOptionalNote"),
                  })}
                >
                  <input
                    value={form.subject}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, subject: e.target.value }))
                    }
                    className={inputCls}
                    placeholder={
                      channelType === "Email"
                        ? t("sales.salesTemplates.modal.subjectRequiredPlaceholder")
                        : t("sales.salesTemplates.modal.subjectOptionalPlaceholder")
                    }
                    disabled={saving}
                  />
                </Box>

                <Box label={t("sales.salesTemplates.modal.activeLabel")}>
                  <label className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked={!!form.isActive}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, isActive: e.target.checked }))
                      }
                      className="w-5 h-5"
                      disabled={saving}
                    />
                    <span>{t("sales.salesTemplates.modal.isActive")}</span>
                  </label>
                </Box>

                <div className="md:col-span-2">
                  <Box label={t("sales.salesTemplates.modal.contentLabel")}>
                    <textarea
                      value={form.content}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, content: e.target.value }))
                      }
                      className={textareaCls}
                      rows={8}
                      placeholder={t("sales.salesTemplates.modal.contentPlaceholder")}
                      disabled={saving}
                    />
                  </Box>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 mt-6">
                <button
                  onClick={saving ? undefined : onClose}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl font-semibold bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-40"
                  type="button"
                >
                  {t("common.cancel")}
                </button>

                <button
                  onClick={submit}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 shadow-lg hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
                  type="button"
                >
                  <CheckCircle className="w-5 h-5" />
                  {saving ? t("sales.salesTemplates.saving") : t("sales.salesTemplates.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function Box({ label, children }) {
  return (
    <div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-xl">
      <label className="text-sm text-gray-200">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";

const textareaCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30 resize-y";
